import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CloudRain, Mountain, Snowflake, Droplets, Layers, BrickWall,
  CheckCircle2, AlertTriangle, OctagonAlert, ChevronDown, Phone, Eye, Wrench, ArrowRight,
} from "lucide-react";
import { usePageTitle } from "../hooks/usePageTitle";
import { SeverityChecker } from "../components/guide/SeverityChecker";

const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    { "@type": "Question", "name": "When should I worry about a crack in my foundation or concrete?", "acceptedAnswer": { "@type": "Answer", "text": "Hair-line cracks under 1/16 inch are usually cosmetic and DIY friendly. Cracks with minor water seepage should be monitored and scheduled for service. Structural cracks wider than 1/4 inch — especially with water intrusion — are urgent: call a contractor immediately." } },
    { "@type": "Question", "name": "My retaining wall is leaning — is that dangerous?", "acceptedAnswer": { "@type": "Answer", "text": "A lean over 2 inches off vertical means hydrostatic pressure is winning and the wall should be professionally assessed this season. Active movement, soil collapse or severe bulging is urgent — keep people away from the wall and call immediately." } },
    { "@type": "Question", "name": "Water pools near my foundation after rain. What should I do?", "acceptedAnswer": { "@type": "Answer", "text": "First clear grates and extend downspouts 5–10 ft from the house. If water is still standing 24 hours after rain, your perimeter drainage needs professional attention. Water entering a crawlspace or basement is urgent." } },
    { "@type": "Question", "name": "What is the white powder on my brick or concrete?", "acceptedAnswer": { "@type": "Answer", "text": "That's efflorescence — mineral salts left behind by water moving through masonry. It's a warning sign of moisture movement. Clean gently with a soft wash, and if it keeps returning, have the drainage behind the surface assessed." } },
    { "@type": "Question", "name": "What property maintenance should BC homeowners do each season?", "acceptedAnswer": { "@type": "Answer", "text": "Spring: inspect for freeze-thaw damage and clear perimeter drains. Summer: the dry window for masonry, drainage and wall work. Autumn: clear leaves from gutters and drains and fix micro-cracks before the rains. Winter: watch for pooling water and damp foundation walls during heavy rain." } },
    { "@type": "Question", "name": "Is it safe to fix a rock wall myself?", "acceptedAnswer": { "@type": "Answer", "text": "Only minor hand-stacked landscape rock adjustments under 2 feet high, and clearing weep holes and drains. Anything taller, leaning, bulging or retaining a slope needs professional equipment and drainage design." } }
  ]
};

function useFaqJsonLd() {
  useEffect(() => {
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.id = "guide-faq-jsonld";
    s.text = JSON.stringify(FAQ_JSONLD);
    document.head.appendChild(s);
    return () => { document.getElementById("guide-faq-jsonld")?.remove(); };
  }, []);
}

const CLIMATE = [
  {
    icon: CloudRain,
    title: "Heavy Rain & Coastal Wet Seasons",
    body: "Atmospheric rivers can drop a month of rain in days. Extreme soil saturation overloads perimeter drains, backs water up against foundations, and turns stable ground into moving ground.",
  },
  {
    icon: Mountain,
    title: "Slopes & Hillsides",
    body: "Pacific Northwest topography means many homes sit on or below grade changes. Saturated slopes erode, and hydrostatic pressure builds silently against foundations and retaining walls until something gives.",
  },
  {
    icon: Snowflake,
    title: "Freeze-Thaw & Soil Types",
    body: "Clay holds water; glacial till drains unevenly. When seasonal frost hits saturated soil, it expands — heaving slabs, widening mortar joints, and shifting rock walls a little more every winter.",
  },
];

const SEASONS = [
  {
    key: "spring",
    label: "Spring",
    accent: "#4C8C4A",
    items: [
      "Walk the property after the last freeze — look for new cracks, heaved pavers and shifted rocks from freeze-thaw cycles.",
      "Clear winter debris from perimeter drains, catch basins and window wells before the spring rains.",
      "Sight down rock and retaining walls: any new lean, bulge or soil pushing through joints since fall?",
      "Check crawlspaces and basements for dampness left behind by winter saturation.",
    ],
  },
  {
    key: "summer",
    label: "Summer",
    accent: "#C98A1B",
    items: [
      "Dry ground = the ideal window for structural work: masonry repointing, drainage re-routing and rock wall construction.",
      "Book major excavation or drainage projects now — contractors and soil conditions are at their best.",
      "Check soil compaction around the foundation; topped-up, properly graded soil should slope AWAY from the house.",
      "Reseal patios, steps and exposed hardscapes while surfaces are warm and dry.",
    ],
  },
  {
    key: "autumn",
    label: "Autumn",
    accent: "#B4552D",
    items: [
      "Clear leaves from gutters, downspouts and lawn catch drains — this is the #1 cause of winter water damage.",
      "Inspect foundations and masonry for micro-cracks BEFORE the rains start; small gaps become big problems when saturated.",
      "Check that downspout splash blocks and extensions carry water at least 5–10 ft from the foundation.",
      "Test perimeter drains with a hose — slow drainage now means flooding in November.",
    ],
  },
  {
    key: "winter",
    label: "Winter",
    accent: "#2F5F80",
    items: [
      "During heavy rain events, watch for pooling water near the foundation — note anything still standing 24h later.",
      "Monitor basement and crawlspace walls for new damp patches: the first sign of hydrostatic pressure.",
      "Mitigate ice dams on hardscapes and walkways; freeze-thaw on standing water destroys surfaces fast.",
      "Photograph retaining walls monthly — comparing photos is the easiest way to catch slow movement.",
    ],
  },
];

const TOPICS = [
  {
    icon: Droplets,
    title: "Perimeter & Property Drainage",
    look: [
      "Water pooling near the foundation after rain",
      "Slow perimeter drains or gurgling downspouts",
      "Wet or musty crawlspaces",
      "Soil erosion channels across lawns or beds",
    ],
    diy: [
      "Clear grates, catch basins and window wells of debris",
      "Extend downspouts a minimum of 5–10 ft from the house",
      "Keep surface trenches and swales clear of leaves and silt",
    ],
  },
  {
    icon: Layers,
    title: "Rockwalls & Gravity Retaining Walls",
    look: [
      "Bulging rocks or a visible lean developing",
      "Soil pushing through the joints between rocks",
      "No gravel backing or weep drainage behind the wall",
      "Standing water above or behind the wall after rain",
    ],
    diy: [
      "Clear weep holes and wall-base drains of debris",
      "Minor hand-stacked landscape rock readjustments — under 2 ft high only",
      "Keep heavy loads (vehicles, soil piles) back from the top of the wall",
    ],
  },
  {
    icon: BrickWall,
    title: "Masonry, Brick & Hardscapes",
    look: [
      "Efflorescence — white powdery salt deposits (a water-movement warning)",
      "Crumbling or sandy mortar joints (spalling)",
      "Hair-line cracking on patios, steps and walkway slabs",
    ],
    diy: [
      "Gentle soft-wash cleaning — never high-pressure on aging mortar",
      "Minor sealant reapplication on hairline surface cracks",
      "Top up polymeric joint sand on paver surfaces",
    ],
  },
];

const SEVERITY = [
  {
    level: "Green · Mild",
    tag: "DIY FRIENDLY",
    color: "#3E7C3C",
    bg: "bg-[#3E7C3C]",
    icon: CheckCircle2,
    items: [
      "Minor cosmetic hair-line cracks (under 1/16\")",
      "Surface leaf clogs in grates and gutters",
      "Downspout splash blocks needing extension",
    ],
    note: "Handle it on a dry weekend — then keep an eye on it.",
  },
  {
    level: "Yellow · Moderate",
    tag: "MONITOR & SCHEDULE SERVICE",
    color: "#C98A1B",
    bg: "bg-[#C98A1B]",
    icon: AlertTriangle,
    items: [
      "Surface pooling still present 24 hours after rain",
      "Wall leaning more than 2 inches off vertical",
      "Cracking mortar with minor water seepage",
    ],
    note: "Photograph it, monitor weekly, and book an assessment this season.",
  },
  {
    level: "Red · Urgent",
    tag: "CALL US IMMEDIATELY",
    color: "#B3372B",
    bg: "bg-[#B3372B]",
    icon: OctagonAlert,
    items: [
      "Standing water invading crawlspace or basement",
      "Active wall movement or soil collapse",
      "Structural foundation cracks wider than 1/4\"",
      "Severe hydrostatic bulging on retaining walls",
    ],
    note: "Don't wait for the next storm — call (250) 886-1112 now.",
  },
];

function SeverityCard({ s }) {
  const Icon = s.icon;
  return (
    <div className="bg-white border-2 border-[#2C3539]/10 flex flex-col" style={{ borderTopColor: s.color, borderTopWidth: "6px" }} data-testid={`severity-${s.tag.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="p-6 md:p-7 flex-1">
        <div className="flex items-center gap-3">
          <span className={`w-10 h-10 ${s.bg} text-white inline-flex items-center justify-center shrink-0`}><Icon size={20} /></span>
          <div>
            <div className="font-display text-2xl uppercase text-[#23282B] leading-none">{s.level}</div>
            <div className="text-[10px] font-bold tracking-[0.22em] uppercase mt-1" style={{ color: s.color }}>{s.tag}</div>
          </div>
        </div>
        <ul className="mt-5 space-y-2.5">
          {s.items.map((it) => (
            <li key={it} className="flex items-start gap-2 text-sm text-[#23282B]/85">
              <span className="w-1.5 h-1.5 mt-[7px] shrink-0" style={{ background: s.color }} />
              {it}
            </li>
          ))}
        </ul>
      </div>
      <div className="px-6 md:px-7 py-4 border-t border-[#2C3539]/10 text-xs font-semibold text-[#23282B]/70">{s.note}</div>
    </div>
  );
}

function TopicAccordion({ t, idx }) {
  const [open, setOpen] = useState(idx === 0);
  const Icon = t.icon;
  return (
    <div className="bg-white border-2 border-[#2C3539]/10" data-testid={`guide-topic-${idx}`}>
      <button
        onClick={() => setOpen(!open)}
        data-testid={`guide-topic-toggle-${idx}`}
        className="w-full flex items-center gap-4 p-5 md:p-6 text-left hover:bg-[#F1F3F5]/60 transition-colors"
      >
        <span className="w-12 h-12 bg-[#2F5F80] text-white inline-flex items-center justify-center shrink-0"><Icon size={22} /></span>
        <span className="flex-1 font-display text-2xl md:text-3xl uppercase text-[#23282B] leading-tight">{t.title}</span>
        <ChevronDown size={22} className={`text-[#2F5F80] shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="grid grid-cols-1 md:grid-cols-2 border-t-2 border-[#2C3539]/10">
          <div className="p-5 md:p-7 md:border-r-2 border-[#2C3539]/10">
            <div className="flex items-center gap-2 text-[#C98A1B] text-xs font-bold tracking-[0.25em] uppercase"><Eye size={15} /> What to Look For</div>
            <ul className="mt-4 space-y-2.5">
              {t.look.map((it) => (
                <li key={it} className="flex items-start gap-2 text-sm text-[#23282B]/85">
                  <span className="w-1.5 h-1.5 mt-[7px] bg-[#C98A1B] shrink-0" />{it}
                </li>
              ))}
            </ul>
          </div>
          <div className="p-5 md:p-7 bg-[#3E7C3C]/[0.04] border-t-2 md:border-t-0 border-[#2C3539]/10">
            <div className="flex items-center gap-2 text-[#3E7C3C] text-xs font-bold tracking-[0.25em] uppercase"><Wrench size={15} /> Safe Homeowner DIY Fixes</div>
            <ul className="mt-4 space-y-2.5">
              {t.diy.map((it) => (
                <li key={it} className="flex items-start gap-2 text-sm text-[#23282B]/85">
                  <CheckCircle2 size={15} className="text-[#3E7C3C] mt-0.5 shrink-0" />{it}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

const CURRENT_SEASON = ["winter", "winter", "spring", "spring", "spring", "summer", "summer", "summer", "autumn", "autumn", "autumn", "winter"][new Date().getMonth()];

export default function Guide() {
  usePageTitle("Homeowner's Guide", "Seasonal property maintenance checklists, drainage and foundation deep-dives, and a free AI photo severity checker for BC homeowners.");
  useFaqJsonLd();
  const [season, setSeason] = useState(CURRENT_SEASON);
  const active = SEASONS.find((s) => s.key === season);

  return (
    <div className="pt-20 md:pt-24" data-testid="guide-page">
      {/* HERO */}
      <section className="bg-[#23282B] text-white">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-16 py-16 md:py-24">
          <div className="text-[#9CC4DB] text-xs tracking-[0.35em] font-bold uppercase">Free Homeowner Resource</div>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95] max-w-4xl">
            Homeowner's Guide to Property Care & Structural Health
          </h1>
          <p className="mt-5 max-w-2xl text-white/80 text-base md:text-lg">
            Drainage, rockwalls & masonry in coastal BC — what the climate does to your property,
            what you can safely fix yourself, and exactly when to call us.
          </p>
        </div>
      </section>

      {/* SECTION 1 — CLIMATE */}
      <section className="bg-[#F1F3F5]" data-testid="guide-climate">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-16 py-16 md:py-24">
          <div className="text-[#2F5F80] text-xs tracking-[0.35em] font-bold uppercase">Why Here, Why Now</div>
          <h2 className="mt-2 font-display text-4xl md:text-5xl uppercase text-[#23282B]">Built on wet, moving ground.</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {CLIMATE.map(({ icon: Icon, title, body }) => (
              <div key={title} className="bg-white border-2 border-[#2C3539]/10 p-7">
                <span className="w-12 h-12 bg-[#2F5F80] text-white inline-flex items-center justify-center"><Icon size={22} /></span>
                <div className="mt-4 font-display text-2xl uppercase text-[#23282B] leading-tight">{title}</div>
                <p className="mt-3 text-sm text-[#23282B]/75 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 2 — SEASONAL TABS */}
      <section className="bg-white" data-testid="guide-seasons">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-16 py-16 md:py-24">
          <div className="text-[#2F5F80] text-xs tracking-[0.35em] font-bold uppercase">Year-Round Maintenance</div>
          <h2 className="mt-2 font-display text-4xl md:text-5xl uppercase text-[#23282B]">The seasonal checklist.</h2>

          <div className="mt-8 grid grid-cols-2 sm:flex gap-2" role="tablist" aria-label="Seasons">
            {SEASONS.map((s) => (
              <button
                key={s.key}
                role="tab"
                aria-selected={season === s.key}
                onClick={() => setSeason(s.key)}
                data-testid={`guide-season-${s.key}`}
                className={`min-h-[48px] px-5 sm:px-8 font-display text-xl uppercase tracking-wide border-2 transition-colors ${
                  season === s.key ? "text-white" : "border-[#2C3539]/20 text-[#23282B]/70 hover:border-[#2F5F80] hover:text-[#2F5F80]"
                }`}
                style={season === s.key ? { background: s.accent, borderColor: s.accent } : {}}
              >
                {s.label}
              </button>
            ))}
          </div>

          <div className="mt-6 border-2 border-[#2C3539]/10 p-6 md:p-8" style={{ borderLeftColor: active.accent, borderLeftWidth: "6px" }} data-testid="guide-season-panel">
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4">
              {active.items.map((it) => (
                <li key={it} className="flex items-start gap-3 text-[15px] text-[#23282B]/85">
                  <CheckCircle2 size={18} className="mt-0.5 shrink-0" style={{ color: active.accent }} />
                  {it}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* SECTION 3 — DEEP DIVES */}
      <section className="bg-[#F1F3F5]" data-testid="guide-topics">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-16 py-16 md:py-24">
          <div className="text-[#2F5F80] text-xs tracking-[0.35em] font-bold uppercase">Deep Dives</div>
          <h2 className="mt-2 font-display text-4xl md:text-5xl uppercase text-[#23282B]">Know your three weak points.</h2>
          <div className="mt-10 space-y-4">
            {TOPICS.map((t, i) => <TopicAccordion key={t.title} t={t} idx={i} />)}
          </div>
        </div>
      </section>

      {/* SECTION 4 — SEVERITY MATRIX */}
      <section className="bg-[#23282B]" data-testid="guide-severity">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-16 py-16 md:py-24">
          <div className="text-[#9CC4DB] text-xs tracking-[0.35em] font-bold uppercase">The Severity Matrix</div>
          <h2 className="mt-2 font-display text-4xl md:text-5xl uppercase text-white">When to call us.</h2>
          <p className="mt-3 text-white/70 max-w-2xl text-sm md:text-base">Match what you're seeing to a tier. When in doubt, send us a photo — assessments are free.</p>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {SEVERITY.map((s) => <SeverityCard key={s.level} s={s} />)}
          </div>
          <SeverityChecker />
        </div>
      </section>

      {/* SECTION 5 — CTA */}
      <section className="bg-[#2F5F80] text-white" data-testid="guide-cta">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-16 py-16 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <h2 className="font-display text-4xl md:text-5xl uppercase leading-[0.95]">Unsure about a crack or water issue on your property?</h2>
              <p className="mt-4 text-white/85 text-base md:text-lg max-w-2xl">
                Get an expert on-site assessment before a minor seasonal issue turns into costly structural damage.
                Free written estimates — 48 hour turnaround, urgent drainage and wall-movement calls dispatched first.
              </p>
            </div>
            <div className="lg:col-span-4 flex flex-col gap-3">
              <a href="tel:+12508861112" data-testid="guide-cta-call" className="inline-flex items-center justify-center gap-2 bg-white text-[#2F5F80] hover:bg-[#F1F3F5] text-sm font-bold tracking-[0.15em] px-7 py-4 uppercase transition-colors">
                <Phone size={16} /> Call (250) 886-1112
              </a>
              <Link to="/contact" data-testid="guide-cta-quote" className="inline-flex items-center justify-center gap-2 border-2 border-white/60 hover:border-white hover:bg-white/10 text-white text-sm font-bold tracking-[0.15em] px-7 py-3.5 uppercase transition-colors">
                Request a Quote <ArrowRight size={16} />
              </Link>
              <div className="text-xs text-white/70 text-center">Urgent wall movement or flooding? Call — we dispatch same-day assessments.</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
